import { Edit } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PopoverAG from "components/ui/popoverAG";
import useBase from "hooks/useBase";
import useFormatter from "hooks/useFormatter";
import { setLoading } from "lib/Reducer/loadingSlice";
import moment from "moment";
import { BsChevronExpand } from "react-icons/bs";
import { MdDelete, MdEdit } from "react-icons/md";
import { TbListDetails } from "react-icons/tb";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  updateFollowupDate,
  updateProjectCustomerStatus,
} from "../../api/projectCustomer";
import useQueries from "hooks/useQueries";

const MainTableRow = ({
  row,
  columns,
  span,
  handleDelete,
  setReload,
  openNote,
  setOpenNote,
  setOpenAdmission,
  openAdmission,
  handleTableCheckBox,
  selectedRows,
  allCustomers,
  setAllCustomers,
  permissions,
  setModal,
  setOpenConfirmTransfer,
  ...restProps
}) => {
  const { setQueries } = useQueries();
  const { base } = useBase();
  const { projectCustomerStatuses } = base;
  const { formatDate } = useFormatter();

  const handleChangeStatus = async (id) => {
    if (projectCustomerStatuses?.find((item) => item?.id === id)?.is_inactive) {
      setOpenConfirmTransfer({
        row_id: row?.id,
        status_id: id,
      });
      return;
    }

    setAllCustomers(
      allCustomers.map((customer) =>
        customer.id === row.id
          ? {
              ...row,
              status_id: id,
              status_color: projectCustomerStatuses?.find(
                (pcs) => pcs?.id === id
              ).color,
            }
          : customer
      )
    );

    const result = await updateProjectCustomerStatus({
      body_data: {
        project_customer_id: row?.id,
        project_customer_status_id: id,
      },
    });

    if (!result.success) {
      toast.error(result.message);
    }
  };

  let backgroundColor = "";

  const dispatch = useDispatch();

  const handleFollowupDate = async (followup_date) => {
    dispatch(setLoading(true));
    const body_data = {
      project_customer_id: row?.id,
      followup_date,
    };
    try {
      const result = await updateFollowupDate({ body_data });
      if (result.success) {
        toast.success(result.message);
        setReload((pre) => !pre);
      } else {
        toast.error(result.message);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <TableRow
      {...restProps}
      sx={{
        "& .MuiTableCell-sizeMedium": {
          padding: "10px 0px 10px 16px",
          backgroundColor: row.alert > 0 ? "#fff1f1" : "",
        },
        "& .MuiTableCell-sizeMedium:first-of-type": {
          paddingLeft: "8px",
          paddingRight: "0px",
        },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedRows?.includes(row.id)}
          onChange={(e) => handleTableCheckBox(row.id, e.target.checked)}
          disabled={!permissions["bulk action"]}
        />
      </TableCell>
      {columns.map((column) => {
        const value = row[column.id];
        return (
          <TableCell key={column.id} align={column.align} style={column.style}>
            {column.id === "created_at" ? (
              formatDate(value)
            ) : column.id === "university_name" ? (
              value ? (
                <Box
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    "&:hover": {
                      "& svg": {
                        color: (theme) => theme.palette.primary.main,
                      },
                    },
                    "& svg": {
                      position: "absolute",
                      color:
                        openAdmission === row?.id
                          ? (theme) => theme.palette.primary.main
                          : "#777",
                      top: "0px",
                      right: "0px",
                      cursor: "pointer",
                      fontSize: "17px",
                    },
                  }}
                  onClick={() => {
                    setOpenAdmission(openAdmission === row?.id ? "" : row?.id);
                    setOpenNote("");
                  }}
                >
                  <Typography
                    sx={{
                      overflow: "hidden",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                      maxWidth: "150px",
                      maxHeight: "22px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {value}
                  </Typography>
                  <BsChevronExpand />
                </Box>
              ) : (
                "-"
              )
            ) : column.id === "admission_status" ? (
              <Box
                sx={{
                  bgcolor: `${row?.admission_status_color}15`,
                  color: row?.admission_status_color,
                  textAlign: "center",
                  padding: "3px 5px",
                  borderRadius: "20px",
                  width: "100px",
                  mt: "5px",
                  fontSize: "13px",
                }}
              >
                {value}
              </Box>
            ) : column.id === "status_id" ? (
              <TextField
                size="small"
                value={value}
                select
                sx={{
                  width: "130px",
                  textAlign: "left",
                  "& > div:before": { content: "none" },
                  "& div": {
                    color: row?.status_color,
                  },
                  "& svg": {
                    color: row?.status_color,
                  },
                }}
                variant="standard"
                onChange={(e) => handleChangeStatus(e.target.value)}
              >
                {projectCustomerStatuses?.length > 0 &&
                  projectCustomerStatuses?.map((status) => (
                    <MenuItem value={status?.id} key={status?.id}>
                      {status?.name}
                    </MenuItem>
                  ))}
              </TextField>
            ) : column.id === "last_note" ? (
              <Box
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  "&:hover": {
                    "& svg": {
                      color: (theme) => theme.palette.primary.main,
                    },
                  },
                  "& svg": {
                    position: "absolute",
                    color:
                      openNote === row?.id
                        ? (theme) => theme.palette.primary.main
                        : "#777",
                    top: "0px",
                    right: "0px",
                    cursor: "pointer",
                    fontSize: "17px",
                  },
                }}
                onClick={() => {
                  setOpenNote(openNote === row?.id ? "" : row?.id);
                  setOpenAdmission("");
                }}
              >
                <Typography
                  sx={{
                    overflow: "hidden",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    maxHeight: "22px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {value || "-"}
                </Typography>
                <MdEdit />
              </Box>
            ) : column.id === "followup_date" ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{formatDate(value)}</span>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Custom input"
                    value={value}
                    onChange={(newValue) => {
                      handleFollowupDate(moment(newValue).format("YYYY-MM-DD"));
                    }}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {InputProps?.endAdornment}
                        <input
                          style={{
                            width: "0px",
                            height: "0px",
                            opacity: 0,
                          }}
                          ref={inputRef}
                          {...inputProps}
                        />
                      </Box>
                    )}
                  />
                </LocalizationProvider>
              </Box>
            ) : column.id === "assignees" ? (
              <PopoverAG items={value} />
            ) : column.id === "action" ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Details">
                  <IconButton onClick={() => setQueries(`id=${row?.id}&m=1`)}>
                    <TbListDetails />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    disabled={!permissions.update}
                    onClick={() => setQueries(`id=${row?.id}&m=0`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => handleDelete(row.id)}
                    disabled={!permissions.trash}
                  >
                    <MdDelete />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              value || "-"
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default MainTableRow;
